import {
  Modal,
  ModalBody,
  ModalTitle,
  ModalFooter,
  ModalFooterAlignment,
  MODAL_INNER_GAP,
} from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { client, Drug, Patient } from '../../api';
import { ExtraInfoTable } from 'design-system/src/components/ExtraInfoTable/ExtraInfoTable';
import { useNavigate } from 'react-router-dom';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { tasksQueryKey } from '../../queries';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';

export type AdditionalInput = {
  needs_draft_review?: boolean;
};

type SubmitAppealModalProps = {
  closeModal: () => void;
  patient: Patient;
  drug: Drug;
  taskId: string;
  includeEnrollmentNotice: boolean;
};

const SubmitAppealModal = ({ closeModal, patient, drug, taskId, includeEnrollmentNotice }: SubmitAppealModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const extraInfoData = [
    { label: 'Patient', value: `${patient?.first_name} ${patient?.last_name}` },
    { label: 'Drug', value: drug?.name },
  ];

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const hasEnrollmentWaitingForStart = useQuery({
    queryKey: ['appeals', 'has-enrollment-awaiting-start', taskId],
    queryFn: async () => {
      const response = await client.get(`appeals/${taskId}/has-enrollment-awaiting-start`);
      return response.data;
    },
  });

  const handleTaskResponse = async () => {
    if (isTrainingMode) {
      console.warn(`Training Mode: Mocking "submit appeal" for task ID: ${taskId}`);
      toast.custom(() => <Toast variant="success">Appeal letter approved.</Toast>);
      navigate('/tasks');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('task_response', 'approve_and_send');
      setIsSubmitting(true);
      const response = await client.post(`tasks/${taskId}/respond`, formData);
      toast.custom(() => <Toast variant="success">Appeal letter approved.</Toast>);
      if (response.data.redirect_portal_task_id) {
        navigate(`/tasks/${response.data.redirect_portal_task_id}`);
      } else {
        navigate('/tasks');
      }
      queryClient.invalidateQueries({
        queryKey: tasksQueryKey(),
      });
    } catch (error) {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  let modalCopy;
  if (includeEnrollmentNotice) {
    modalCopy = `Tandem will obtain patient consent (if required), submit the appeal letter to the patient's plan, and start the enrollment process for Bridge or Patient Assistance (PAP).`;
  } else {
    modalCopy = `Tandem will obtain patient consent (if required) and submit the appeal letter to the patient's plan.`;
  }

  const redirectCopy = `By clicking "Submit," you'll be redirected to complete the form signature task for Bridge or Patient Assistance (PAP), if needed.`;

  return (
    <Modal onClose={closeModal}>
      <ModalTitle>Submit appeal</ModalTitle>

      <ModalBody>
        <ExtraInfoTable
          data={extraInfoData}
          minFieldLabelWidth="80px"
          marginTop="0"
          tableMarginBottom={MODAL_INNER_GAP}
          hairlineComponent={<Hairline $marginTop="0" $marginBottom="0" />}
        />

        {hasEnrollmentWaitingForStart.isLoading ? (
          <LoadingSpinner height="60px" />
        ) : (
          <>
            <Typography>{modalCopy}</Typography>
            {hasEnrollmentWaitingForStart.data?.has_enrollment_waiting_for_start && (
              <Typography>{redirectCopy}</Typography>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter align={ModalFooterAlignment.SpaceBetween}>
        <Button variant="tertiary" onClick={closeModal}>
          Back
        </Button>
        <Button onClick={handleTaskResponse} isLoading={isSubmitting}>
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SubmitAppealModal;
